import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/delivery.scss";

const deliveryJourney = [
  {
    title: "Bestellung bestätigt",
    text: "Nach Eingang erhältst du eine Zusammenfassung an die angegebene E-Mail-Adresse.",
  },
  {
    title: "Sorgfältig verpackt",
    text: "Wir stellen deine Artikel zusammen und verpacken sie so ressourcenschonend wie möglich.",
  },
  {
    title: "Unterwegs zu dir",
    text: "Sobald das Paket übergeben wurde, erhältst du eine Versandbestätigung mit Sendungsverfolgung.",
  },
];

function Delivery() {
  const {
    brandName,
    locale = "de-DE",
    currency = "EUR",
    freeShippingThreshold = 99,
    shippingFee = 4.95,
    contact = {},
  } = siteConfig;
  const { email } = contact;
  const money = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  });
  const formattedFee = money.format(Number(shippingFee) || 0);
  const formattedThreshold = money.format(Number(freeShippingThreshold) || 0);

  return (
    <main className="delivery-page">
      <header className="delivery-page__hero">
        <div className="delivery-page__hero-art" aria-hidden="true">
          <span className="delivery-page__route delivery-page__route--one" />
          <span className="delivery-page__route delivery-page__route--two" />
          <span className="delivery-page__parcel">→</span>
        </div>
        <div className="delivery-page__hero-copy">
          <nav
            className="delivery-page__breadcrumb"
            aria-label="Brotkrümelnavigation"
          >
            <Link to="/">Startseite</Link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">Versand</span>
          </nav>
          <p className="delivery-page__eyebrow">Von uns zu dir</p>
          <h1>Versand mit Sorgfalt.</h1>
          <p>
            Wir halten dich vom Eingang der Bestellung bis zur Zustellung auf
            dem Laufenden – klar, verlässlich und so ressourcenschonend wie möglich.
          </p>
        </div>
      </header>

      <section
        className="delivery-page__highlights"
        aria-label="Wichtige Versandinformationen"
      >
        <div>
          <span aria-hidden="true">01</span>
          <strong>Kostenfreier Versand</strong>
          <p>ab {formattedThreshold} Bestellwert</p>
        </div>
        <div>
          <span aria-hidden="true">02</span>
          <strong>Sendungsverfolgung</strong>
          <p>mit der Versandbestätigung</p>
        </div>
        <div>
          <span aria-hidden="true">03</span>
          <strong>Bewusst verpackt</strong>
          <p>materialsparend und passend zum Inhalt</p>
        </div>
      </section>

      <section className="delivery-page__options" aria-labelledby="shipping-options-title">
        <div className="delivery-page__section-heading">
          <p className="delivery-page__eyebrow">Versandarten</p>
          <h2 id="shipping-options-title">Wann kommt mein Paket?</h2>
          <p>
            Die konkrete Lieferzeit siehst du beim Artikel und vor Abschluss der
            Bestellung. An Sonn- und Feiertagen erfolgt keine Zustellung.
          </p>
        </div>

        <div className="delivery-page__table-wrap" tabIndex="0">
          <table>
            <caption className="delivery-page__visually-hidden">
              Übersicht über Versandarten und Kosten
            </caption>
            <thead>
              <tr>
                <th scope="col">Versandart</th>
                <th scope="col">Voraussichtliche Laufzeit</th>
                <th scope="col">Kosten</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Standardversand</th>
                <td>2–4 Werktage nach Versand</td>
                <td>{formattedFee}</td>
              </tr>
              <tr>
                <th scope="row">Ab {formattedThreshold}</th>
                <td>2–4 Werktage nach Versand</td>
                <td>Kostenfrei</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="delivery-page__table-note">
          Für einzelne Artikel oder Lieferorte können abweichende Laufzeiten und
          Zuschläge gelten. Diese werden im Checkout vor der Bestellung angezeigt.
        </p>
      </section>

      <section className="delivery-page__journey" aria-labelledby="delivery-journey-title">
        <div className="delivery-page__section-heading">
          <p className="delivery-page__eyebrow">Deine Bestellung</p>
          <h2 id="delivery-journey-title">So kommt sie auf den Weg</h2>
        </div>
        <ol>
          {deliveryJourney.map((step, index) => (
            <li key={step.title}>
              <span aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
              <div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="delivery-page__packaging" aria-labelledby="packaging-title">
        <div className="delivery-page__packaging-art" aria-hidden="true">
          <span />
        </div>
        <div className="delivery-page__packaging-copy">
          <p className="delivery-page__eyebrow">Weniger ist mehr</p>
          <h2 id="packaging-title">Verpackt mit Blick aufs Wesentliche</h2>
          <p>
            {brandName} stimmt die Verpackungsgröße auf den Inhalt ab und
            verzichtet, wo möglich, auf unnötiges Füllmaterial. Bewahre die
            Verpackung gerne für eine mögliche Retoure oder zur weiteren Nutzung auf.
          </p>
        </div>
      </section>

      <section className="delivery-page__faq" aria-labelledby="delivery-faq-title">
        <div>
          <p className="delivery-page__eyebrow">Fragen zum Versand</p>
          <h2 id="delivery-faq-title">Kurz beantwortet</h2>
        </div>
        <div className="delivery-page__faq-list">
          <details>
            <summary>Kann ich meine Lieferadresse nachträglich ändern?</summary>
            <p>
              Solange die Bestellung noch nicht verarbeitet wurde, prüfen wir
              gerne, ob eine Änderung möglich ist. Kontaktiere uns dafür möglichst früh.
            </p>
          </details>
          <details>
            <summary>Was passiert, wenn ich bei der Zustellung nicht da bin?</summary>
            <p>
              Der Versanddienst informiert dich über einen erneuten Versuch,
              einen Ablageort oder die Abholung in einer nahegelegenen Filiale.
            </p>
          </details>
          <details>
            <summary>Meine Sendungsverfolgung bewegt sich nicht.</summary>
            <p>
              Statusmeldungen können zeitversetzt erscheinen. Bleibt der Status
              mehrere Werktage unverändert, hilft unser Service bei der Klärung.
            </p>
          </details>
        </div>
        <div className="delivery-page__help">
          <p>Du brauchst persönliche Unterstützung?</p>
          {email && <a href={`mailto:${email}`}>{email}</a>}
          <Link to="/contact">Kontakt aufnehmen</Link>
        </div>
      </section>
    </main>
  );
}

export default Delivery;
